import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '../services/api';
import inviteApi from '../services/inviteApi';

const STORAGE_KEY = 'lastWorkspaceId';

export const useWorkspaceStore = defineStore('workspace', () => {
  const workspaces = ref([]);
  const currentWorkspace = ref(null);
  const loading = ref(false);
  const invites = ref([]);
  const pendingInviteCount = computed(() => invites.value.length);

  const currentWorkspaceId = computed(() => currentWorkspace.value?._id ?? null);

  async function loadWorkspaces() {
    loading.value = true;
    try {
      const res = await api.get('/workspaces');
      if (res.data.success) {
        workspaces.value = res.data.data;
      }
    } finally {
      loading.value = false;
    }
  }

  function resolveWorkspace(requestedId) {
    if (!workspaces.value.length) return null;

    if (requestedId) {
      const found = workspaces.value.find(w => w._id === requestedId);
      if (found) return found;
    }

    // Default: oldest workspace
    const sorted = [...workspaces.value].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
    return sorted[0];
  }

  function switchWorkspace(workspace) {
    currentWorkspace.value = workspace;
    if (workspace) {
      localStorage.setItem(STORAGE_KEY, workspace._id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  function getLastWorkspaceId() {
    return localStorage.getItem(STORAGE_KEY);
  }

  async function createWorkspace(name) {
    const res = await api.post('/workspaces', { name });
    if (res.data.success) {
      await loadWorkspaces();
      const created = workspaces.value.find(w => w._id === res.data.data._id) ?? res.data.data;
      switchWorkspace(created);
      return created;
    }
    throw new Error('Failed to create workspace');
  }

  async function renameWorkspace(workspaceId, name) {
    const res = await api.put(`/workspaces/${workspaceId}`, { name });
    if (res.data.success) {
      const serverName = res.data.data?.name ?? name;
      const ws = workspaces.value.find(w => w._id === workspaceId);
      if (ws) ws.name = serverName;
      if (currentWorkspace.value?._id === workspaceId) {
        currentWorkspace.value = { ...currentWorkspace.value, name: serverName };
      }
    }
  }

  // Shared local-state removal used by delete, leave and the access-revoked handler.
  function forgetWorkspace(workspaceId) {
    workspaces.value = workspaces.value.filter(w => w._id !== workspaceId);
    if (currentWorkspace.value?._id === workspaceId) {
      switchWorkspace(workspaces.value[0] ?? null);
    }
  }

  async function deleteWorkspace(workspaceId) {
    await api.delete(`/workspaces/${workspaceId}`);
    forgetWorkspace(workspaceId);
  }

  async function leaveWorkspace(workspaceId) {
    const res = await api.post(`/workspaces/${workspaceId}/leave`);
    if (res.data.success) {
      forgetWorkspace(workspaceId);
    }
    return res.data;
  }

  async function loadInvites() {
    try {
      const res = await inviteApi.getMyInvites();
      if (res.success) {
        invites.value = res.data;
      }
    } catch {
      invites.value = [];
    }
  }

  // Backwards-compatible alias: callers that only needed the count
  // can keep using the same name.
  const loadPendingInviteCount = loadInvites;

  async function acceptInvite(inviteId) {
    const res = await inviteApi.acceptInvite(inviteId);
    if (res.success) {
      invites.value = invites.value.filter(i => i._id !== inviteId);
      await loadWorkspaces();
    }
    return res;
  }

  async function declineInvite(inviteId) {
    const res = await inviteApi.declineInvite(inviteId);
    if (res.success) {
      invites.value = invites.value.filter(i => i._id !== inviteId);
    }
    return res;
  }

  return {
    workspaces,
    currentWorkspace,
    currentWorkspaceId,
    loading,
    invites,
    pendingInviteCount,
    loadWorkspaces,
    resolveWorkspace,
    switchWorkspace,
    getLastWorkspaceId,
    createWorkspace,
    renameWorkspace,
    forgetWorkspace,
    deleteWorkspace,
    leaveWorkspace,
    loadInvites,
    loadPendingInviteCount,
    acceptInvite,
    declineInvite,
  };
});
